import { Injectable } from '@angular/core';
import { emptyRota, RotaVo } from '~model/vo/rota-vo';
import { MonitoramentoService } from '../api/monitoramento.service';
import { EventSourceService } from '../utils/event-source.service';
import { select, Store } from '@ngrx/store';
import {upsertEntityRotas} from '~entity/entity-rota/entity-rota.actions';
import {environment} from '~env/environment';
import {asyncScheduler, BehaviorSubject, from, Observable, of, Subscription} from 'rxjs';
import {LoggerService} from '../utils/logger.service';
import {getAuth} from '~feature/feature-auth/feature-auth.selectors';
import {IndicadoresService} from './indicadores.service';
import {CardsService} from './cards.service';
import {entityRotaSelectAll} from '~entity/entity-rota/entity-rota.selectors';
import {bufferCount, concatMap, delay, filter, first, observeOn} from 'rxjs/operators';
import {ParamService} from '../utils/param.service';
import {UpdateService} from '../utils/update.service';
import {filtroSetInput} from '~feature/feature-filtro/feature-filtro.actions';
import {FiltroFactory} from '~model/filtro/filtro-factory';
import {emptyVersion, VersionVo} from '~model/vo/version-vo';
import {RotaService} from '~core/api/rota.service';
import {Map, OrderedMap} from 'immutable';
import {base64} from '~utils/encode';
import {VisitaVo} from '~model/vo/visita-vo';
import {AppConfigService} from '~core/background/app-config.service';
import {LSItem, StorageUtils} from '~utils/storage-utils';
import { KeyValue } from '~utils/value-key';
import { ChatService } from '~core/api/chat.service';
import { CardGroupVo } from '~model/vo/card-group-vo';
import { AbstractMqttService } from '~core/api/mqtt/abstract-mqtt.service';
import { PendenciaRota } from '~model/vo/pendencia-rota';
import {ProxyInterceptor} from '~core/inteceptors/proxy.interceptor';
import {distinct} from '~utils/list-utils';
import { HttpErrorResponse } from '@angular/common/http';
import {AuthService} from '~core/security/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable()
export class CoreService {
  carregando$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  exibirLayout$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private eventSourceRotas!: EventSource;
  private failCount!: number;
  private authSubscription?: Subscription;
  private worker!: Worker;
  private eventKey: string = '';
  private _version$: BehaviorSubject<VersionVo> = new BehaviorSubject<VersionVo>(emptyVersion());
  newEvent$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  firstRouteLoad$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  workerReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private ctService: MonitoramentoService,
    private rotaService: RotaService,
    private events: EventSourceService,
    private logger: LoggerService,
    private indicadoresService: IndicadoresService,
    private cardsService: CardsService,
    private appConfigService: AppConfigService,
    private param: ParamService,
    private store: Store<RotaVo>,
    private updatesService: UpdateService,
    private chatService: ChatService,
    private mqttService: AbstractMqttService,
    private authService: AuthService,
    private snackbar: MatSnackBar
  ) {
    this.inicializarWorkers().then(() => {
      this.workerReady$.next(true);
    }).catch(err => console.error(err));
  }

  private setVersion(version: VersionVo): void {
    const codigoOperacaoNew = version.codigoOperacao;
    const percProcessAnt = this.version.percProcess;

    if (!codigoOperacaoNew) {
      version.percProcess = percProcessAnt;
    }

    this._version$.next(version);
  }

  private get version(): VersionVo {
    return this._version$.value;
  }

  getVersion(): Observable<VersionVo> {
    return this._version$;
  }

  inicializar(): void {
    this.store.dispatch(filtroSetInput({filtros: [FiltroFactory.build('', [''])]}));
    this.carregando$.next(true);

    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
    this.authSubscription = this.store.select(getAuth).pipe(
      observeOn(asyncScheduler)
    ).subscribe(({token, roles}) => {
      if (token) {
        let exibir = true;
        if (roles?.includes('Admin')) {
          this.buscaConfiguracoes();
          // Start process admin
        } else if (roles?.includes('Financeiro')) {
          // Start process financeiro
          this.buscaConfiguracoes();
          this.getVersions(true);
        } else if (roles?.includes('Operador')) {
          this.indicadoresService.inicializar();
          this.cardsService.inicializar();
          this.inicializarEventsSources();
          if (environment.experimentalFeatures?.showChat) {
            this.mqttService?.inicializar();
            this.chatService.inicializar();
          }
          this.popularTabela();
        }  else if (roles?.includes('Custom')) {
          this.indicadoresService.inicializar();
          this.cardsService.inicializar();
          this.inicializarEventsSources();
          if (environment.experimentalFeatures?.showChat) {
            this.mqttService?.inicializar();
            this.chatService.inicializar();
          }
          this.popularTabela();
        }else {
          exibir = false;
        }
        this.exibirLayout$.next(exibir);
      } else {
        this.encerraEventos();
      }
      this.carregando$.next(false);
    });

    if (StorageUtils.reload) {
      setInterval(() => {
        if (StorageUtils.reload) {
          this.logger.debug('reload');
          this.workerReady$.pipe(filter(ready => ready), first()).subscribe(() => {
            this.popularTabela();
            StorageUtils.reload = '';
          });
        }
      }, 5000);
    }
  }

  popularTabela(): void {
    if (!this.workerReady$.value) {
      this.logger.debug('Aguardando worker para popularTabela');
      this.workerReady$.pipe(filter(ready => ready), first()).subscribe(() => this.popularTabela());
      return;
    }
    this.buscaConfiguracoes();
    this.updateIndicadores();
    this.getVersions();
  }

  getVersions(onlyVersion?: boolean): void {
    this.ctService.getVersions(this.param.dataMonitoramento).pipe(
      observeOn(asyncScheduler, 500)
    ).subscribe(
      res => {
        this.processaVersion(res, true, onlyVersion);
      },
      (error: HttpErrorResponse) => {
        if (error.status === 401) {
          this.authService.desativar();
        } else if (error.status === 404) {
          // Tratamento silencioso: não exibe erro, não interrompe, apenas emite emptyVersion
          this.processaVersion(emptyVersion(), true, onlyVersion);
        }
        // Outros erros são ignorados silenciosamente
      }
    );
  }

  async inicializarWorkers(): Promise<void> {
    this.worker = new Worker(
      new URL('../workers/processar-rota-no-imports.worker', import.meta.url),
      { type: 'module', name: 'ProcessarRota' }
    );
    this.worker.onmessage = (value: MessageEvent<{data: string, empresa: string, rotas: RotaVo[]}>) => {
      const {data, empresa, rotas} = value.data;
      if (!this.checaReferencias(data, empresa)) {
        return;
      }
      const entities: RotaVo[] = rotas || [];
      entities.forEach(rota => {
        const mapper = (l: VisitaVo[]) => l.map(v => v.codigo);
        const reducer = <T>(l: T[], v: T[]) => l.concat(v);
        rota.visitasIds = Map(rota.visitaDias).map(mapper).toList().reduce(reducer, [] as string[]);
        rota.cds = this.getVisitas(rota, 'TIPO_CD').toList().reduce(reducer, [] as VisitaVo[]);

        // ⭐ NOVO: Salvar uma cópia COMPLETA com TODOS os tipos antes de filtrar
        (rota as any).visitaDiasCompleto = {...rota.visitaDias};

        // Manter comportamento original: visitaDias fica só com TIPO_PEDIDO
        rota.visitaDias = this.getVisitas(rota, 'TIPO_PEDIDO').toJSON();
        rota.pendencia = rota.pendencia || PendenciaRota.SEM_PENDENCIA;
      });
      this.store.dispatch(upsertEntityRotas({entities}) as any);
    };
  }

  /**
   * ⭐ Filtra visitas por tipo (usado internamente pelo worker para manter compatibilidade)
   */
  getVisitas(rota: RotaVo, tipo: string): Map<string, VisitaVo[]> {
    return Map(rota.visitaDias).map(l => l.filter(i => i.tipo === tipo));
  }

  processaIndicadores(res: CardGroupVo[]): void {
    this.cardsService.processaIndicadores(res);
  }

  inicializarEventsSources(): void {
    this.ctService.getSseId().pipe(filter(v => !!v)).subscribe(({id}) => {
      const version = base64(localStorage.getItem(LSItem.version) ?? '0');

      const key = `${id}:${version}`;
      if (this.param.isNow) {
        this.eventKey = key;
        this.getNewEventSource(key);
      } else {
        this.eventKey = '';
      }
    });
  }

  checaFalhasEvento(key: string): void {
    this.failCount = this.failCount || 0;
    this.encerraCheckEventos();
    StorageUtils.idxInterval = setInterval(() => {
      if (this.eventSourceRotas.readyState === EventSource.CLOSED && this.param.isNow) {
        this.failCount++;
        this.getNewEventSource(key);
        this.logger.debug('eventos closed');
      } else {
        this.failCount = 0;
      }
      if (this.failCount > 10 || !localStorage.getItem(LSItem.token)) {
        this.encerraCheckEventos();
        this.inicializarEventsSources();
      }
    }, 10000);
  }

  encerraEventos(): void {
    if (this.eventSourceRotas) {
      this.eventSourceRotas.close();
      this.encerraCheckEventos();
    }
    if (environment.experimentalFeatures?.showChat) {
      this.chatService.limpar();
    }
  }

  encerraCheckEventos(): void {
    StorageUtils.idxIntervals.forEach(idx => clearInterval(Number(idx)));
    localStorage.setItem(LSItem.intervals, '[]');
  }

  private getNewEventSource(key: string): void {
    this.encerraEventos();
    this.checaFalhasEvento(key);
    this.events.setUrl(`${ProxyInterceptor.controlTower()}/versions?id=${key}`);
    this.eventSourceRotas = this.events.getInstance();

    this.eventSourceRotas.onmessage = ({data}) => {
      try {
        this.encerraEventos();
        const vo: VersionVo = JSON.parse(data);
        if (this.param.dataMonitoramento === vo.data) {
          this.newEvent$.next(true);
          this.processaVersion(vo);
          setTimeout(() => this.newEvent$.next(false), 2000);
        }
      } catch (e) {
        this.logger.error('Falha em evento: ', e);
      } finally {
        setTimeout(() => this.inicializarEventsSources(), 10000);
      }
    };
    this.eventSourceRotas.onopen = ({currentTarget}) => {
      const eventKey = this.eventKey;
      const target = currentTarget as EventSource;
      if (!eventKey || !target?.url || !target?.url.includes(eventKey)) {
        target.close();
      }
    };
  }

  private processaVersion(vo: VersionVo, force?: boolean, onlyVersion?: boolean): void {
    const keys = Object.keys(vo.versions ?? {}).concat(...(vo.routes ?? [])).filter(distinct);
    if (force) {
      this.firstRouteLoad$.next(true);
      this.setVersion({...vo, routes: keys, routesProcessed: [], percProcess: 0});
    }
    const vAtual = localStorage.getItem(LSItem.version) ?? 0;
    localStorage.setItem(LSItem.version, `${vo.version || 0}`);
    if (force || (vAtual !== vo.version && vo.versions)) {
      this.store.pipe(
        select(entityRotaSelectAll), first()
      ).subscribe(result => {
        const rotas = result.reduce((arr, val) => arr.concat(val), [] as { id: string, version: number }[]);
        const atualizar: KeyValue<number> = vo.versions || {};
        const update = vo.routes?.reduce((acc, val, idx) => {
          const version = vo.routesVersions?.[idx] ?? 0;
          if (rotas.find(r => r.id === val && r.version === version)) {
            return acc;
          }
          return acc.set(val, version);
        }, OrderedMap<number>({}));
        rotas.forEach(r => (atualizar[r.id] === r.version && delete atualizar[r.id]));
        this.logger.debug('Versoes para atualizar: ', atualizar);

        const data = this.param.dataMonitoramento;
        const empresa = this.appConfigService.empresa;
        if (update?.size || JSON.stringify(atualizar) !== '{}') {
          const map = update ?? Map(atualizar);
          if (force) {
            const array = map.keySeq().toArray();
            const routesProcessed = keys.filter(k => !array.includes(k));
            const percProcess = keys.length !== 0 ? routesProcessed.length / keys.length * 100 : 0;
            this.setVersion({...vo, routes: keys, routesProcessed, percProcess});
          }
          const subscription = from(map).pipe(
            bufferCount(20),
            concatMap((item, i) => of(item).pipe(delay(i ? 2000 : 0)))
          ).subscribe(items => {
            if (!this.checaReferencias(data, empresa)) {
              subscription.unsubscribe();
              return;
            }
            this.updateRota(data, empresa, items);
            if (force) {
              const oldVo = this.version;
              const routesProcessed = (oldVo.routesProcessed ?? []).concat(items.map(([i]) => i)).filter(distinct);
              const percProcess = keys.length !== 0 ? routesProcessed.length / keys.length * 100 : 0;
              this.setVersion({...oldVo, routesProcessed, percProcess});
            }
          });
        } else {
          this.updateRota(data, empresa, []);
        }
      });
      if (!onlyVersion) {
        this.updateIndicadores();
      }
    }
  }

  private buscaConfiguracoes(): void {
    this.appConfigService.atualizaConfiguracoes();
  }

  private updateIndicadores(): void {
    const data = this.param.dataMonitoramento;
    this.logger.debug('updateIndicadores: dataMonitoramento', data);
    this.logger.debug('updateIndicadores: endpoint', `${ProxyInterceptor.controlTower()}/indicadores?data=${data}`);
    this.ctService.getIndicadores(data).subscribe(res => this.processaIndicadores(res));
  }

  private updateRota(data: string, empresa: string, items: [string, number][]): void {
    this.logger.debug('atualizaRota', items);
    if (!this.checaReferencias(data, empresa)) {
      return;
    }
    if (items.length) {
      this.rotaService.getRotas(items.map(([i]) => i)).subscribe(res => {
        this.processaRotas(data, empresa, res);
      });
    } else {
      this.processaRotas(data, empresa, []);
    }
  }

  private processaRotas(data: string, empresa: string, rotas: RotaVo[]): void {
    const rotasSafe = (rotas || []).map(rota => ({...emptyRota(rota)}));
    if (!this.worker) {
      this.logger.error('Worker não inicializado! Ignorando processaRotas.', {data, empresa, rotas: rotasSafe});
      return;
    }
    this.worker.postMessage({data, empresa, rotas: rotasSafe});
  }

  private checaReferencias(data: string, empresa: string): boolean {
    const novaData = this.param.dataMonitoramento;
    const novaEmpresa = this.appConfigService.empresa;
    if (data !== novaData || empresa !== novaEmpresa) {
      this.logger.debug('Empresa|data divergentes', [data, empresa, novaData, novaEmpresa]);
      return false;
    }
    return true;
  }

  buscarAtualizacao(): void {
    this.updatesService.inicializar();
  }
}
